import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ErrorDetailsDto, ReportResultType } from 'types';
import styles from './styles.module.scss';
import { CloseIcon } from '@entur/icons';
import { Heading5, Paragraph } from '@entur/typography';
import { Loader } from '@entur/loader';
import { IconButton, PrimaryButton } from '@entur/button';
import classNames from 'classnames/bind';
import { useCreateReportOrder } from './api/createReportOrder';
import { useCreateDownloadJob } from './api/createDownloadJob';
import { useDownloadJob } from './api/getDownloadJob';
import { isAxiosError } from 'axios';

interface AsyncResultReportProps {
  data: ReportResultType;
}

const AsyncResultReport = ({ data: { reports, reportOrderPath, reportOrderBody, format } }: AsyncResultReportProps) => {
  const intl = useIntl();
  const cx = classNames.bind(styles);

  const [isClosedByUser, setIsClosedByUser] = useState(false);

  const {
    mutate: createReportOrder,
    error: createReportOrderError,
    data: createReportOrderData,
    isPending: createReportOrderIsPending,
    isSuccess: createReportOrderIsSuccess,
  } = useCreateReportOrder();

  const {
    mutate: createDownloadJob,
    error: createDownloadJobError,
    data: createDownloadJobData,
    isPending: createDownloadJobIsPending,
    isSuccess: createDownloadJobIsSuccess,
  } = useCreateDownloadJob();

  // Derive downloadJobId from mutation result instead of state
  const downloadJobId = useMemo(
    () => (createDownloadJobData?.data?.id ? String(createDownloadJobData.data.id) : null),
    [createDownloadJobData]
  );

  const { data: downloadJobData, error: downloadJobError } = useDownloadJob({
    id: downloadJobId || '',
    queryConfig: {
      enabled: !!downloadJobId,
      retry: false,
      refetchInterval: (query) => {
        if (!downloadJobId) return false;
        const data = query.state.data;
        if (data?.data?.signedBucketUrl) return false;
        return 1500;
      },
    },
  });

  // Only use signedBucketUrl if it matches the current downloadJobId
  // This prevents stale data from previous downloads blocking polling
  const signedBucketUrl =
    downloadJobData?.data && String(downloadJobData.data.id) === downloadJobId
      ? downloadJobData.data.signedBucketUrl
      : undefined;

  // Only use downloadJobError if it matches the current downloadJobId
  // This prevents stale errors from previous downloads showing as failures
  const currentDownloadJobError =
    downloadJobError && downloadJobData?.data && String(downloadJobData.data.id) === downloadJobId
      ? downloadJobError
      : null;

  // Compute report order IDs early since it's needed for activeServiceCall logic
  const reportOrderIds = useMemo(() => {
    if (reports) {
      return reports.map((report) => report.id).join(',');
    }

    const reportOrderResponse = createReportOrderData;
    const reportOrderData = reportOrderResponse?.data;
    if (reportOrderData) {
      return Array.isArray(reportOrderData) ? String(reportOrderData[0].id) : String(reportOrderData.id);
    }

    return '';
  }, [reports, createReportOrderData]);

  // Derive which phase we're in based on data availability
  const activeServiceCall = useMemo((): 'POST_ORDER' | 'POST_DOWNLOAD' | 'FETCH_DOWNLOAD' => {
    if (downloadJobId) {
      return 'FETCH_DOWNLOAD';
    }
    // If download job has been attempted (success, pending, or error), we're in POST_DOWNLOAD phase
    if (createDownloadJobIsSuccess || createDownloadJobIsPending || createDownloadJobError) {
      return 'POST_DOWNLOAD';
    }
    // If we have report order IDs but haven't started download job yet, we're transitioning to POST_DOWNLOAD
    if (reportOrderIds) {
      return 'POST_DOWNLOAD';
    }
    return 'POST_ORDER';
  }, [downloadJobId, createDownloadJobIsSuccess, createDownloadJobIsPending, createDownloadJobError, reportOrderIds]);

  const downloadFileFromUrl = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatError = (errorText?: string | null) => {
    if (!errorText) return '';
    return errorText.length > 500 ? `${errorText.substring(0, 500)}...` : errorText;
  };

  const getTitle = () => {
    if (reports) {
      return reports[0].filename ?? '';
    }

    const reportOrderData = createReportOrderData?.data;
    if (reportOrderData) {
      const firstReportOrder = Array.isArray(reportOrderData) ? reportOrderData[0] : reportOrderData;
      return `${firstReportOrder.reportTemplateId} ${firstReportOrder.cleosFilename}`;
    }

    return '';
  };

  const getInfoMessage = () => {
    if (activeServiceCall === 'POST_ORDER') {
      if (createReportOrderIsSuccess) return intl.formatMessage({ id: 'asyncResult.reportOrdered' });
      if (createReportOrderError) {
        if (isAxiosError<ErrorDetailsDto>(createReportOrderError)) {
          const status = createReportOrderError.response?.status;
          if (status === 400) return intl.formatMessage({ id: 'asyncResult.errorData' });
          if (status === 404) return intl.formatMessage({ id: 'asyncResult.reportTemplateNotExist' });
          if (status === 409)
            return intl.formatMessage(
              { id: 'asyncResult.reportConflict' },
              { error: formatError(createReportOrderError.message) }
            );
        }
        return intl.formatMessage({ id: 'asyncResult.reportFailed' });
      }
    }

    if (activeServiceCall === 'POST_DOWNLOAD') {
      if (createDownloadJobIsSuccess) {
        if (createDownloadJobData?.data.signedBucketUrl) return intl.formatMessage({ id: 'asyncResult.ok' });
        return intl.formatMessage({ id: 'asyncResult.downloadJobCreated' });
      }
      if (createDownloadJobError) {
        if (isAxiosError<ErrorDetailsDto>(createDownloadJobError)) {
          const status = createDownloadJobError.response?.status;
          if (status === 400) return intl.formatMessage({ id: 'asyncResult.errorData' });
          if (status === 404) return intl.formatMessage({ id: 'asyncResult.reportNotExist' });
          if (status === 204) return intl.formatMessage({ id: 'asyncResult.reportNoContent' });
          if (status === 409)
            return intl.formatMessage(
              { id: 'asyncResult.reportConflict' },
              { error: formatError(createDownloadJobError.message) }
            );
        }
        return intl.formatMessage({ id: 'asyncResult.reportFailed' });
      }
    }

    if (activeServiceCall === 'FETCH_DOWNLOAD') {
      // Handle errors from the download job query before showing "not ready"
      if (currentDownloadJobError) {
        if (isAxiosError<ErrorDetailsDto>(currentDownloadJobError)) {
          const status = currentDownloadJobError.response?.status;
          if (status === 204) return intl.formatMessage({ id: 'asyncResult.reportNoContent' });
          if (status === 400) return intl.formatMessage({ id: 'asyncResult.errorData' });
          if (status === 404) return intl.formatMessage({ id: 'asyncResult.downloadJobNotExist' });
          if (status === 409)
            return intl.formatMessage(
              { id: 'asyncResult.reportConflict' },
              { error: formatError(currentDownloadJobError.message) }
            );
          if (status === 410) return intl.formatMessage({ id: 'asyncResult.downloadJobExpired' });
        }
        return intl.formatMessage(
          { id: 'asyncResult.reportFailed' },
          { error: formatError(currentDownloadJobError.message) }
        );
      }

      if (signedBucketUrl) return intl.formatMessage({ id: 'asyncResult.reportFinished' });
      if (downloadJobId) return intl.formatMessage({ id: 'asyncResult.reportNotReady' });
    }
  };

  // Order new report if no report orders are provided
  useEffect(() => {
    if (!reports && reportOrderPath) {
      createReportOrder({ path: reportOrderPath, data: reportOrderBody ?? null });
    }
  }, [reportOrderBody, reportOrderPath, reports, createReportOrder]);

  // Start download job with the provided/returned report order ids
  useEffect(() => {
    if (reportOrderIds && !createDownloadJobIsPending && !createDownloadJobIsSuccess && !createDownloadJobError) {
      createDownloadJob({ reportOrderIds, format });
    }
  }, [
    reportOrderIds,
    format,
    createDownloadJob,
    createDownloadJobIsPending,
    createDownloadJobIsSuccess,
    createDownloadJobError,
  ]);

  // Download file when download job completes
  useEffect(() => {
    if (signedBucketUrl) {
      downloadFileFromUrl(signedBucketUrl);
    }
  }, [signedBucketUrl]);

  const handleRerunClick = () => {
    if (!reports && reportOrderPath) {
      createReportOrder({ path: reportOrderPath, data: reportOrderBody ?? null });
    } else {
      if (reportOrderIds) {
        createDownloadJob({ reportOrderIds, format });
      }
    }
  };

  // Compute visibility: show unless user closed it or download completed
  const hasDownloadCompleted = !!signedBucketUrl;
  const shouldShowNotification = !isClosedByUser && !hasDownloadCompleted;

  if (!shouldShowNotification) return null;

  const hasError = !!(createReportOrderError || createDownloadJobError || currentDownloadJobError);
  const isPending =
    !hasError && (createReportOrderIsPending || createDownloadJobIsPending || (!!downloadJobId && !signedBucketUrl));

  return (
    <div
      className={cx({
        asyncResultContainer: true,
        info: true,
        error: hasError,
        success: hasDownloadCompleted,
      })}
    >
      <div className={styles.headingContainer}>
        <Heading5 className={styles.heading}>{getTitle()}</Heading5>
        <IconButton aria-label="Lukk" onClick={() => setIsClosedByUser(true)}>
          <CloseIcon />
        </IconButton>
      </div>
      {isPending ? (
        <Loader>{intl.formatMessage({ id: 'asyncResult.getting' })} rapport</Loader>
      ) : (
        <>
          <Paragraph>{getInfoMessage()}</Paragraph>
          {hasError && (
            <PrimaryButton onClick={handleRerunClick}>{intl.formatMessage({ id: 'asyncResult.rerun' })}</PrimaryButton>
          )}
        </>
      )}
    </div>
  );
};

export default AsyncResultReport;
